import type { ReactElement } from 'react';

/* One icon per navigation *group* — the eight tiles of the icon rail.
 *
 * **Groups, not entries, since the redesign.** The rail is now 72px of eight tiles and a group's
 * entries are text tabs in the top bar, so a glyph identifies a group and nothing else needs one. The
 * thirty per-entry glyphs this file used to carry had no remaining consumer and went with the
 * sidebar that drew them.
 *
 * **Hand-written SVG paths rather than an icon package**, and that is hard rule 13 rather than a
 * preference: a webfont icon set fetches from a CDN, and the packages that ship paths locally add a
 * dependency whose licence and air-gap story would need checking for eight glyphs. The paths are the
 * design's own (`Lumiq Redesign.dc.html`): house, cylinder, gauge, padlock, spanner, bubble, package,
 * people.
 *
 * **Keyed on the group id**, which `navigation.test.ts` already holds distinct, and a missing id
 * renders nothing rather than a placeholder — a fallback glyph would put one mark beside two
 * destinations, which is worse than an absence on a rail read by shape.
 */

/* 18px, 1.8px stroke, round caps and joins — the design's values. `currentColor`, so the active tile's
 * ink moves the glyph with it rather than needing rules of its own. A plain function rather than a
 * component, so this module exports no component beside a non-component and fast refresh keeps
 * working. */
function glyph(children: ReactElement): ReactElement {
  return (
    <svg
      className="lq-rail__glyph"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const ICONS: Readonly<Record<string, ReactElement>> = {
  home: glyph(
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V20h14V9.5" />
      <path d="M9.5 20v-5.5h5V20" />
    </>,
  ),
  data: glyph(
    <>
      <ellipse cx="12" cy="5.5" rx="7.5" ry="2.8" />
      <path d="M4.5 5.5v6c0 1.55 3.36 2.8 7.5 2.8s7.5-1.25 7.5-2.8v-6" />
      <path d="M4.5 11.5v6c0 1.55 3.36 2.8 7.5 2.8s7.5-1.25 7.5-2.8v-6" />
    </>,
  ),
  quality: glyph(
    <>
      <path d="M3.5 16a8.5 8.5 0 1 1 17 0" />
      <path d="M12 16l4.2-4.6" />
    </>,
  ),
  governance: glyph(
    <>
      <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" />
      <path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7" />
    </>,
  ),
  action: glyph(
    <path d="M15.6 3.6a5 5 0 0 0-1.2 8.1L4.5 21.5l-2-2L12.3 9.6A5 5 0 0 0 20.4 8.4l-3.2-1.6-1.6-3.2Z" />,
  ),
  ai: glyph(
    <path d="M20.5 12c0 4.1-3.8 7.4-8.5 7.4a9.8 9.8 0 0 1-2.7-.4L4.5 20.5l1.3-3.6A7 7 0 0 1 3.5 12c0-4.1 3.8-7.4 8.5-7.4s8.5 3.3 8.5 7.4Z" />,
  ),
  platform: glyph(
    <>
      <path d="M20.5 8.2v7.6L12 20.5 3.5 15.8V8.2L12 3.5l8.5 4.7Z" />
      <path d="M3.5 8.2 12 12.9l8.5-4.7" />
      <path d="M12 12.9v7.6" />
    </>,
  ),
  admin: glyph(
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.2 20.5c0-3.2 2.6-5.4 5.8-5.4s5.8 2.2 5.8 5.4" />
      <path d="M16 5.2a3.2 3.2 0 0 1 0 6.1" />
      <path d="M17.4 15.4c2.1.5 3.4 2.2 3.4 4.4" />
    </>,
  ),
};

export function groupIcon(id: string): ReactElement | null {
  return ICONS[id] ?? null;
}
